
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { TopicType } from '@/types/assistant';
import { getAiMarkdownComponents, getUserMarkdownComponents } from './MarkdownComponents';
import { SuggestionButtons } from './SuggestionButtons';
import { MessageFeedback } from './MessageFeedback';

interface MessageContentProps {
  isAi: boolean;
  displayContent: string;
  isStreaming: boolean;
  topic: TopicType;
  isLatest: boolean;
  suggestions: string[];
  messageId: string;
  feedback?: 'positive' | 'negative' | null;
  onSuggestionClick?: (suggestion: string) => void;
  onFeedbackChange?: (messageId: string, feedback: 'positive' | 'negative' | null) => void;
}

export const MessageContent: React.FC<MessageContentProps> = React.memo(({ 
  isAi,
  displayContent,
  isStreaming,
  topic,
  isLatest,
  suggestions,
  messageId,
  feedback,
  onSuggestionClick,
  onFeedbackChange
}) => {
  const aiComponents = useMemo(() => getAiMarkdownComponents(topic), [topic]);
  const userComponents = useMemo(() => getUserMarkdownComponents(), []);

  if (isAi) {
    return (
      <div 
        className={cn(
          "group relative p-4 rounded-2xl transition-all duration-300 w-full",
          "bg-gradient-to-br from-background/40 to-background/20",
          "backdrop-blur-xl border border-white/10",
          "shadow-lg shadow-black/5",
          isStreaming && [
            "border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-600/10",
            "shadow-blue-500/10 shadow-xl"
          ],
          "hover:border-white/20 hover:shadow-xl hover:shadow-black/10",
          "hover:bg-gradient-to-br hover:from-background/50 hover:to-background/30"
        )}
        style={{
          minHeight: '2.5rem',
          willChange: 'contents',
          contain: 'layout style'
        }}
      >
        <div className={cn(
          "absolute top-0 left-4 right-4 h-px",
          "bg-gradient-to-r from-transparent via-white/20 to-transparent"
        )} />

        <div 
          className="markdown-content relative z-10"
          style={{
            overflowWrap: 'break-word',
            wordBreak: 'break-word'
          }}
        >
          <div className="text-sm leading-[1.5] max-w-none">
            {!displayContent && isStreaming ? (
              <div className="flex items-center space-x-3 text-blue-400 min-h-[2rem]">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium tracking-wide">AI is thinking</span>
                  <motion.div
                    className="flex space-x-1"
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-1.5 h-1.5 bg-current rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </motion.div>
                </div>
              </div>
            ) : displayContent ? (
              <div className="relative">
                <ReactMarkdown components={aiComponents}>
                  {displayContent}
                </ReactMarkdown>
                
                {isStreaming && displayContent && (
                  <motion.span
                    className="inline-block w-2 h-4 bg-blue-400 ml-1 rounded-sm opacity-80"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ 
                      duration: 1, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                    style={{ transform: 'translateZ(0)' }}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>

        {isAi && isLatest && !isStreaming && suggestions.length > 0 && onSuggestionClick && (
          <div className="pt-3 mt-3 border-t border-white/10">
            <SuggestionButtons 
              suggestions={suggestions} 
              topic={topic} 
              onSuggestionClick={onSuggestionClick}
            />
          </div>
        )}

        {isAi && !isStreaming && onFeedbackChange && (
          <div className="flex justify-end mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <MessageFeedback
              messageId={messageId}
              currentFeedback={feedback || null}
              onFeedbackChange={onFeedbackChange}
              topic={topic}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      "relative px-4 py-3 rounded-2xl shadow-md transition-all duration-200",
      "bg-gradient-to-br from-primary/15 to-primary/10",
      "backdrop-blur-sm border border-primary/20",
      "max-w-[85%] ml-auto",
      "hover:shadow-lg hover:border-primary/30",
      "hover:bg-gradient-to-br hover:from-primary/20 hover:to-primary/15"
    )}>
      <div className="absolute top-0 right-4 left-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="markdown-content relative z-10">
        <div className="text-sm leading-[1.5] text-left">
          <ReactMarkdown components={userComponents}>
            {displayContent}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
});

MessageContent.displayName = "MessageContent";
